/* Decorative image loading only. Never changes image URLs, lazy loading or commerce state. */
(() => {
  if (window.spImageLoadingReady) return;
  window.spImageLoadingReady = true;

  const hosts = '.sp-card-image, .sp-collection-image, .sp-hero, .product__media, .media, .product-media-modal__content, .sp-article, .sp-cart-image, .cart-item__image-container, .sp-recommendation-image, .sp-search-hit-image, .sp-pdp-set-card, .rte';
  const excluded = '.sp-card-swatch, .thumbnail, .thumbnail-list, .sp-header, .sp-footer, .shopify-app-block, [data-sp-image-loading="off"]';
  const states = new WeakMap();
  const pending = new Set();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const position = (state) => {
    if (!state.loader || !state.image.isConnected) return;
    const image = state.image.getBoundingClientRect();
    const host = state.host.getBoundingClientRect();
    const scaleX = host.width / state.host.offsetWidth || 1;
    const scaleY = host.height / state.host.offsetHeight || 1;
    state.loader.style.left = `${(image.left + image.width / 2 - host.left) / scaleX - state.host.clientLeft + state.host.scrollLeft}px`;
    state.loader.style.top = `${(image.top + image.height / 2 - host.top) / scaleY - state.host.clientTop + state.host.scrollTop}px`;
  };

  const clear = (state) => {
    clearTimeout(state.timer);
    state.resize?.disconnect();
    state.resize = null;
    state.loader?.remove();
    state.loader = null;
    state.image.classList.remove('sp-site-image-pending');
    intersection?.unobserve(state.image);
    pending.delete(state);
  };

  const finish = async (state, success) => {
    const sequence = state.sequence;
    if (success && state.loader && state.image.decode) {
      try { await state.image.decode(); } catch { /* The native load/error state remains authoritative. */ }
    }
    if (sequence !== state.sequence) return;
    const reveal = success && state.loader && state.image.naturalWidth > 0 && !motion.matches;
    clear(state);
    if (reveal && !state.image.classList.contains('sp-card-secondary')) {
      state.image.classList.add('sp-site-image-reveal');
      state.revealTimer = setTimeout(() => state.image.classList.remove('sp-site-image-reveal'), 350);
    }
  };

  const show = (state) => {
    if (!pending.has(state) || state.loader || !state.image.isConnected) return;
    if (state.image.complete) { finish(state, state.image.naturalWidth > 0); return; }
    const bounds = state.image.getBoundingClientRect();
    if (bounds.width < 50 || bounds.height < 40) return;
    if (getComputedStyle(state.host).position === 'static') state.host.classList.add('sp-site-image-host');
    const loader = document.createElement('span');
    loader.className = 'sp-site-image-loader';
    if (state.image.classList.contains('sp-card-secondary')) loader.classList.add('sp-site-image-loader--secondary');
    loader.setAttribute('aria-hidden', 'true');
    state.host.append(loader);
    state.loader = loader;
    state.image.classList.add('sp-site-image-pending');
    position(state);
    if (window.ResizeObserver) {
      state.resize = new ResizeObserver(() => position(state));
      state.resize.observe(state.image);
      state.resize.observe(state.host);
    }
    // A stalled request must not leave the image permanently hidden.
    state.timer = setTimeout(() => { state.sequence++; clear(state); }, 15000);
  };

  const intersection = window.IntersectionObserver ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => { if (entry.isIntersecting) show(states.get(entry.target)); });
  }) : null;

  const track = (image) => {
    if (!(image instanceof HTMLImageElement) || image.closest(excluded)) return;
    const host = image.closest(hosts);
    if (!host || (!image.getAttribute('src') && !image.getAttribute('srcset'))) return;
    let state = states.get(image);
    if (!state) { state = { image, host, sequence: 0 }; states.set(image, state); }
    clear(state);
    clearTimeout(state.revealTimer);
    image.classList.remove('sp-site-image-reveal');
    state.sequence++;
    state.host = host;
    // Cached/decoded images remain visible, with no artificial loading delay.
    if (image.complete) return;
    pending.add(state);
    if (intersection) intersection.observe(image);
    else show(state);
  };

  const scan = (root) => {
    if (!(root instanceof Element)) return;
    if (root.matches('img')) track(root);
    root.querySelectorAll('img').forEach(track);
  };

  document.addEventListener('load', (event) => {
    const state = states.get(event.target);
    if (state && pending.has(state)) finish(state, true);
  }, true);
  document.addEventListener('error', (event) => {
    const state = states.get(event.target);
    if (state) { state.sequence++; clear(state); }
  }, true);

  new MutationObserver((records) => {
    const changed = new Set();
    records.forEach((record) => {
      if (record.type === 'attributes') {
        if (record.target.matches('img')) changed.add(record.target);
        else record.target.closest('picture')?.querySelectorAll('img').forEach(image => changed.add(image));
      } else {
        if (record.target.matches('picture')) record.target.querySelectorAll('img').forEach(image => changed.add(image));
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches('img')) changed.add(node);
          node.querySelectorAll('img').forEach(image => changed.add(image));
          if (node.matches('source')) node.closest('picture')?.querySelectorAll('img').forEach(image => changed.add(image));
        });
      }
    });
    pending.forEach((state) => { if (!state.image.isConnected) { state.sequence++; clear(state); } });
    changed.forEach(track);
  }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'srcset', 'sizes', 'media'] });

  // Responsive <picture>/srcset choices can change without a DOM mutation.
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => document.querySelectorAll('img').forEach((image) => {
      if (!image.complete && !pending.has(states.get(image))) track(image);
    }), 100);
  }, { passive: true });
  scan(document.body);
})();
